/**
 * Bloque D: Test de imágenes con sistema de preferencias (1-2)
 * 
 * Muestra una imagen central y 6 opciones para calificar con:
 * - 2: Más preferido
 * - 1: Preferido (pero no tanto)
 * 
 * @author Ana Lizcano
 */

import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { styled } from "@mui/material/styles";
import { 
  BaseCard,
  ScrollableArea,
  GridContainer
} from '../../styles/commonStyles';
import { getBlockInstructions } from 'layouts/candidate-test/data/lazyQuestions';
import { InstructionBox } from 'layouts/candidate-test/styles/commonStyles';

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Select, MenuItem, FormControl } from "@mui/material";

// Contenedor principal para la imagen de referencia
const ReferenceImageContainer = styled(BaseCard)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(4),
  backgroundColor: '#ffffff',
  border: '3px solid #667eea',
  textAlign: 'center',
  maxWidth: '200px',
  margin: '0 auto',
  marginBottom: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    right: '-2px',
    bottom: '-2px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '18px',
    zIndex: -1,
  }
}));

// Placeholder para imagen de referencia
const ImagePlaceholder = styled(MDBox)(() => ({
  width: '100%',
  height: '180px',
  backgroundColor: '#f8f9fa',
  border: '2px dashed #dee2e6',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#495057',
  fontSize: '14px',
  fontWeight: 'medium',
  position: 'relative',
  overflow: 'hidden',
}));

// Tarjeta para cada opción de imagen
const OptionCard = styled(BaseCard)(({ theme, preferencevalue }) => {
  let backgroundColor = '#ffffff';
  let borderColor = '#e9ecef';
  let boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
  
  if (preferencevalue === '2') {
    backgroundColor = '#f0f9f0';
    borderColor = '#4caf50';
    boxShadow = '0 4px 16px rgba(76, 175, 80, 0.2)';
  } else if (preferencevalue === '1') {
    backgroundColor = '#fff8f0';
    borderColor = '#ff9800';
    boxShadow = '0 4px 16px rgba(255, 152, 0, 0.2)';
  } else if (preferencevalue === '0') {
    backgroundColor = '#f8f9fa';
    borderColor = '#6c757d';
    boxShadow = '0 2px 8px rgba(108, 117, 125, 0.1)';
  }
  
  return {
    padding: theme.spacing(1.5),
    backgroundColor,
    border: `2px solid ${borderColor}`,
    borderRadius: '12px',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      borderColor: preferencevalue === '2' ? '#4caf50' : preferencevalue === '1' ? '#ff9800' : '#667eea',
    },
  };
});

// Placeholder para opciones de imagen
const OptionImagePlaceholder = styled(MDBox)(({ theme }) => ({
  width: '100%',
  height: '90px',
  backgroundColor: '#ffffff',
  border: '2px solid #e9ecef',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#6c757d',
  fontSize: '10px',
  marginBottom: theme.spacing(1),
  overflow: 'hidden',
  transition: 'border-color 0.3s ease',
  '&:hover': {
    borderColor: '#667eea',
  }
}));

// Select estilizado con colores por preferencia
const StyledSelect = styled(Select)(({ theme, preferencevalue }) => {
  const colors = {
    '2': '#4caf50',  // Verde para más preferido
    '1': '#ff9800',  // Naranja para preferido
    '0': '#6c757d'   // Gris para no seleccionado
  };
  
  const selectedColor = colors[preferencevalue] || '#e9ecef';
  
  return {
    minWidth: '100%',
    height: '38px',
    borderRadius: '8px',
    fontSize: '11px',
    fontWeight: 'bold',
    '& .MuiSelect-select': {
      padding: theme.spacing(0.5),
      backgroundColor: preferencevalue ? selectedColor : '#f5f5f5',
      color: preferencevalue ? '#ffffff' : '#666666',
      border: `2px solid ${selectedColor}`,
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSelect-icon': {
      color: preferencevalue ? '#ffffff' : '#666666',
    },
  };
});

// Badge visual para mostrar la preferencia seleccionada  
const PreferenceBadge = styled(MDBox)(({ value }) => {
  const colors = {
    '2': '#4caf50',
    '1': '#ff9800',
    '0': '#6c757d'
  };
  
  return {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: colors[value],
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: 'bold',
    marginLeft: '4px'
  };
});


const BlockD = ({ question, response, onResponseChange }) => {
  if (!question || question.type !== 'image_preference') return null;

  const responses = response || {};
  const instructionsRef = useRef(null);
  
  useEffect(() => {
    // Scroll to top of the page/container
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Also try to scroll the parent container to top
    const parentContainer = document.querySelector('.MuiBox-root') || document.body;
    if (parentContainer) {
      parentContainer.scrollTop = 0;
    }
  }, [question]);
  
  const handlePreferenceChange = (optionIndex, value) => {
    const newResponses = { ...responses };
    const selectedValue = value === '' ? 0 : parseInt(value);
    
    // Si se selecciona un valor específico (1 o 2), quitar ese valor de otras opciones
    if (selectedValue === 1 || selectedValue === 2) {
      // Buscar si ya existe otra opción con el mismo valor y ponerla en 0
      Object.keys(newResponses).forEach(key => {
        if (parseInt(key) !== optionIndex && newResponses[key] === selectedValue) {
          newResponses[key] = 0;
        }
      });
    }
    
    // Establecer el nuevo valor para la opción actual
    newResponses[optionIndex] = selectedValue;
    
    // Asegurarse de que todas las opciones tengan un valor (0 por defecto)
    for (let i = 0; i < (question.options?.length || 6); i++) {
      if (!(i in newResponses)) {
        newResponses[i] = 0;
      }
    }
    
    onResponseChange(newResponses);
  };


  return (
    <MDBox sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <InstructionBox ref={instructionsRef} variant="info" sx={{ marginBottom: '16px' }}>
        <MDTypography variant="body2" color="info" fontWeight="bold">
          {'Marca con el número 1 cuando sea preferido. Marca con el número 2 cuando sea preferido pero no tanto como el anterior.'}
        </MDTypography>
      </InstructionBox>
      {/* Imagen de referencia */}
      <ReferenceImageContainer>

        <ImagePlaceholder>
          {question.referenceImage ? (
            <img
              src={question.referenceImage}
              alt="Imagen de referencia"
              style={{
                width: '90%',
                height: '90%',
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
          ) : (
            'Espacio para imagen de referencia'
          )}
        </ImagePlaceholder>
      </ReferenceImageContainer>

      <ScrollableArea sx={{ padding: 1 }}>
        <GridContainer columns={6} gap={1.5}>
          {question.options?.map((option, index) => {
            const currentValue = responses[index];
            
            return (
              <OptionCard key={index} preferencevalue={currentValue?.toString()}>
                <OptionImagePlaceholder>
                  {option.image ? (
                    <img
                      src={option.image}
                      alt={`Opción ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        borderRadius: '6px'
                      }}
                    />
                  ) : (
                    `Imagen opción ${index + 1}`
                  )}
                </OptionImagePlaceholder>
                
                <MDBox sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControl sx={{ flex: 1 }}>
                    <StyledSelect
                      value={currentValue !== undefined ? currentValue : 0}
                      onChange={(e) => handlePreferenceChange(index, e.target.value)}
                      preferencevalue={currentValue?.toString()}
                    >
                      <MenuItem value={0}>
                        No seleccionado
                      </MenuItem>
                      <MenuItem value={1}>
                        1 - Más Preferido
                      </MenuItem>
                      <MenuItem value={2}>
                        2 - Preferido
                      </MenuItem>
                    </StyledSelect>
                  </FormControl>
                  <PreferenceBadge value={(currentValue !== undefined ? currentValue : 0).toString()}>
                    {currentValue !== undefined ? currentValue : 0}
                  </PreferenceBadge>
                </MDBox>
              </OptionCard>
            );
          })}
        </GridContainer>
      </ScrollableArea>



    </MDBox>
  );
};

BlockD.propTypes = {
  question: PropTypes.shape({
    type: PropTypes.string.isRequired,
    question: PropTypes.string.isRequired,
    instruction: PropTypes.string.isRequired,
    referenceImage: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
      image: PropTypes.string,
      label: PropTypes.string
    }))
  }).isRequired,
  response: PropTypes.object,
  onResponseChange: PropTypes.func.isRequired
};

export default BlockD;